<template>
    <div class="wallet-modal-backdrop" @click.self="close">
        <div class="modal-card" role="dialog" :aria-label="$t(modalTitleKey)">
            <div class="modal-header">
                <div class="modal-title-row">
                    <h2>{{ $t(modalTitleKey) }}</h2>
                </div>
                <button class="icon-button" type="button" :aria-label="$t('userWallet.close')" @click="close">
                    <img src="/assets/images/wallets/close.svg" alt="" width="22" height="22">
                </button>
            </div>

            <div class="modal-body" v-if="step === 'confirm'" aria-label="unstake confirmation">
                <div class="unstake-summary">
                    <div class="unstake-modal-assets">
                        <div class="unstake-modal-asset">
                            <div class="unstake-modal-asset__name">
                                <img :src="icon" alt="" width="20" height="20">
                                <span>{{ currencyName }}</span>
                            </div>
                            <div class="unstake-modal-asset__amount">{{ amountText }}</div>
                        </div>
                        <p v-if="lastUpdatedText" class="unstake-last-updated">
                            {{ $t('userWallet.unstake.lastUpdatedLabel') }} {{ lastUpdatedText }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="estimatedGasEth !== null || gasLoading || gasEstimateError"
                    class="gas-estimate"
                    :class="{ 'gas-estimate--loading': gasLoading }"
                    aria-live="polite"
                    :aria-busy="gasLoading"
                >
                    <div>
                        <span>{{ $t('userWallet.stake.gasLabel') }}</span>
                        <span class="gas-estimate-value">
                            <span class="gas-estimate-spinner" :class="{ 'is-visible': gasLoading }" aria-hidden="true"></span>
                            <strong v-if="estimatedGasEth !== null">{{ formatAmount(estimatedGasEth) }} ETH</strong>
                        </span>
                    </div>
                    <p v-if="estimatedGasEth !== null" class="gas-estimate-note">
                        <span class="info-icon" aria-hidden="true">i</span>
                        {{ $t('userWallet.unstake.gasNote') }}
                    </p>
                    <p v-if="gasQuoteExpired" class="field-error gas-quote-error" aria-live="polite">
                        {{ $t('userWallet.unstake.gasExpired') }}
                    </p>
                    <p v-if="insufficientGas" class="field-error" aria-live="polite">
                        {{ $t('userWallet.errors.INSUFFICIENT_GAS') }}
                    </p>
                    <button
                        v-if="gasQuoteExpired || gasEstimateError || insufficientGas"
                        class="quote-link-button"
                        type="button"
                        :disabled="gasLoading"
                        @click="requoteGas"
                    >
                        {{ $t('userWallet.unstake.requote') }}
                    </button>
                </div>

                <p class="field-error" aria-live="polite">{{ error || '' }}</p>
            </div>

            <div class="modal-body" v-else aria-label="unstake accepted">
                <div class="completion-mark">
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                        <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                </div>
                <p class="completion-title">{{ $t(acceptedKey) }}</p>
            </div>

            <div class="modal-actions grouped-right">
                <button
                    v-if="step === 'confirm'"
                    class="primary-button"
                    type="button"
                    :disabled="!canSubmit"
                    @click="submit"
                >{{ submitting ? $t(processingKey) : $t(buttonKey) }}</button>
            </div>
        </div>
    </div>
</template>

<script>
    import UserWalletService from '../../../services/UserWalletService';
    import walletFormatMixin from '../../../utils/walletFormatMixin';

    const GAS_QUOTE_TTL_MS = 2 * 60 * 1000;

    export default {
        name: 'WalletUnstakeModal',

        mixins: [walletFormatMixin],

        props: {
            currency: {type: String, required: true}, // 'AUBE' | 'OWL'
            amount: {type: String, required: true},
            withdrawalIds: {type: Array, required: true},
            lastUpdatedAt: {type: String, default: null},
        },

        data() {
            return {
                step: 'confirm', // 'confirm' | 'done'
                submitting: false,
                error: null,
                // 見積り応答が返した確定金額。amount prop は claimable 取得時点の値なので、
                // 再見積り（2分TTL 経過後の requote）のたびにサーバ側の値へ追従させる。
                confirmedAmount: null,
                estimatedGasEth: null,
                gasLoading: false,
                gasQuoteExpired: false,
                gasQuoteTimerId: null,
                gasEstimateError: false,
                // 見積り自体は成功しているがガス代が払えないケース。見積り額は表示したまま
                // エラー文言を出して submit を止める（何ETH足りないか分からないと困るため）。
                insufficientGas: false,
            };
        },

        created() {
            this.fetchEstimatedGas();
        },

        beforeDestroy() {
            this.clearGasQuoteTimer();
        },

        computed: {
            currencyKey() {
                return this.currency.toLowerCase();
            },
            icon() {
                return this.currency === 'OWL'
                    ? '/assets/images/wallets/owl.png'
                    : '/assets/images/wallets/aube.png';
            },
            currencyName() {
                return this.currency === 'OWL' ? 'OWL Token' : 'AUBE Coin';
            },
            modalTitleKey() {
                return `userWallet.unstake.${this.currencyKey}.modalTitle`;
            },
            buttonKey() {
                return `userWallet.unstake.${this.currencyKey}.button`;
            },
            acceptedKey() {
                return `userWallet.unstake.${this.currencyKey}.accepted`;
            },
            processingKey() {
                return `userWallet.unstake.${this.currencyKey}.processing`;
            },
            amountText() {
                const amount = this.confirmedAmount !== null ? this.confirmedAmount : this.amount;
                return `${this.formatAmount(amount)} ${this.currency}`;
            },
            lastUpdatedText() {
                return this.formatShortDateTime(this.lastUpdatedAt);
            },
            canSubmit() {
                return this.estimatedGasEth !== null
                    && !this.gasEstimateError
                    && !this.insufficientGas
                    && !this.gasQuoteExpired
                    && !this.submitting;
            },
        },

        methods: {
            mapErrorCode(e) {
                const code = e.response && e.response.data && e.response.data.error
                    ? e.response.data.error.code
                    : null;
                const key = 'userWallet.errors.' + code;
                return code && this.$te(key) ? this.$t(key) : this.$t('userWallet.errors.stakeFailed');
            },

            async fetchEstimatedGas() {
                if (this.gasLoading) {
                    return;
                }

                this.gasLoading = true;
                try {
                    const response = await UserWalletService.getClaimGasEstimate({withdrawalIds: this.withdrawalIds});
                    this.confirmedAmount = response.data.amount;
                    this.estimatedGasEth = response.data.estimated_gas_fee;
                    this.insufficientGas = response.data.insufficient_gas === true;
                    this.gasEstimateError = false;
                    this.error = null;
                    this.startGasQuoteTimer();
                } catch (e) {
                    this.clearGasQuoteTimer();
                    this.confirmedAmount = null;
                    this.estimatedGasEth = null;
                    this.gasQuoteExpired = false;
                    this.insufficientGas = false;
                    this.gasEstimateError = true;
                    this.error = this.mapErrorCode(e);
                } finally {
                    this.gasLoading = false;
                }
            },

            requoteGas() {
                if (this.gasLoading) {
                    return;
                }
                this.fetchEstimatedGas();
            },

            clearGasQuoteTimer() {
                if (this.gasQuoteTimerId) {
                    clearTimeout(this.gasQuoteTimerId);
                    this.gasQuoteTimerId = null;
                }
            },

            startGasQuoteTimer() {
                this.clearGasQuoteTimer();
                this.gasQuoteExpired = false;
                this.gasQuoteTimerId = setTimeout(() => {
                    this.gasQuoteTimerId = null;
                    this.gasQuoteExpired = true;
                }, GAS_QUOTE_TTL_MS);
            },

            close() {
                if (this.submitting) {
                    return;
                }
                this.$emit('close');
            },

            async submit() {
                if (!this.canSubmit) {
                    return;
                }

                this.error = null;
                this.submitting = true;
                try {
                    await UserWalletService.submitRewardClaim({withdrawalIds: this.withdrawalIds});
                    this.clearGasQuoteTimer();
                    this.step = 'done';
                    this.$emit('claimed', {currency: this.currency});
                } catch (e) {
                    this.error = this.mapErrorCode(e);
                } finally {
                    this.submitting = false;
                }
            },
        },
    };
</script>
